import React from 'react';
import { motion } from 'motion/react';
import { useSettings } from '../context/SettingsContext';
import { getOptimizedImageUrl } from '../utils';

export function About() {
  const { settings } = useSettings();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
  };

  const headerContent = (
    <>
      <motion.div variants={itemVariants} className="mb-6 flex items-center gap-4">
        <span className="h-[1px] w-12 bg-gusto-gold block" />
        <span className="font-serif italic text-[#A67C43] uppercase tracking-[0.2em] text-sm font-medium">
          {settings.about_label}
        </span>
      </motion.div>
      
      <motion.h2 
        variants={itemVariants}
        className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-gusto-dark tracking-tight mb-8 leading-[1.1] whitespace-pre-line"
      >
        {settings.about_title}
      </motion.h2>
    </>
  );

  return (
    <section id="about" className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Tablet Only Header above columns */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="hidden md:block lg:hidden mb-12"
        >
          {headerContent}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 lg:gap-20 items-center">
          
          {/* Text Content */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="order-2 md:order-1"
          >
            {/* Mobile and Desktop Header inside column */}
            <div className="block md:hidden lg:block">
              {headerContent}
            </div>
            
            <motion.p 
              variants={itemVariants}
              className="font-sans text-lg text-gusto-gray leading-relaxed mb-6 whitespace-pre-line"
            >
              {settings.about_text_1}
            </motion.p>
            
            <motion.p 
              variants={itemVariants}
              className="font-sans text-lg text-gusto-gray leading-relaxed mb-10 whitespace-pre-line"
            >
              {settings.about_text_2}
            </motion.p>

            <motion.div variants={itemVariants}>
              <div className="flex gap-8 border-t border-gusto-gray/20 pt-8 mt-4">
                <div>
                  <span className="block font-heading font-bold text-3xl text-gusto-red mb-1">{settings.about_stat_1_val}</span>
                  <span className="text-sm font-medium text-gusto-gray uppercase tracking-wider">{settings.about_stat_1_lbl}</span>
                </div>
                <div>
                  <span className="block font-heading font-bold text-3xl text-gusto-red mb-1">{settings.about_stat_2_val}</span>
                  <span className="text-sm font-medium text-gusto-gray uppercase tracking-wider">{settings.about_stat_2_lbl}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Visual Content */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 md:order-2 relative aspect-[2/3] rounded-[24px] md:rounded-[32px] overflow-hidden"
          >
            {settings.about_image && (
              <picture>
                <source 
                  media="(max-width: 640px)" 
                  srcSet={getOptimizedImageUrl(settings.about_image, 640)} 
                />
                <source 
                  media="(max-width: 1024px)" 
                  srcSet={getOptimizedImageUrl(settings.about_image, 960)} 
                />
                <img 
                  src={getOptimizedImageUrl(settings.about_image, 1200)} 
                  alt={settings.about_title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </picture>
            )}
            {/* Subtle Inner Shadow & Outline */}
            <div className="absolute inset-0 border border-black/5 rounded-[24px] md:rounded-[32px] pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </motion.div>

        </div>
      </div>
    </section>
  );
}
